import { PaymentMethods } from "@/types/db/accounting";
import { LogService } from "@/services/logging";
import POSConfigModule from "@/store/modules/pos-config";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { getPromocodeCampaginsByConfigId } from "@/api/gql/promocode-campaign";
import { IPromocodeCampaign } from "@/types/db/promocode-campaign";
import { getSmeetzPictures } from "@/api/gql/common";
import { dexieDB } from "@/services/dexie-database";
import { atomicTableSwap } from "@/dexie/helpers";
import {
  createCroppedImageproxyUrl,
  SmeetzPictureEntityTables,
  SmeetzPictureEntityTypes,
} from "../helpers/pictures";
import dayjs from "dayjs";

// TODO (ORD-343 — "auto-assign customer info from membership"):
// This feature was dropped during the order-store migration. When a membership /
// membership-sold promo code is applied and no customer is set yet, prod used to
// fetch the membership's original booking via its token (getPSSWithToken +
// getBookingInfo) and copy that customer onto the order (see
// `assignCustomerFromMembership` in the pre-migration version of this file).
// The new order/discount flow no longer exposes a membership token (only
// OrderDiscount.membershipId), so this most likely belongs in the orders package
// (@smeetz/orders / @smeetz/discount-common), which now owns discount-code
// application. Re-introduce it there once a membershipId -> original-booking
// customer lookup is available. Check later.

/**
 * This function extracts the voucher code from a url string
 *
 * Examples of possible url strings we get from the scanner:
 * https//appsmeetz.com/redeemvouchers?vc=UY4W2K3M3T
 * https//appsmeetzcom/redeemvouchersvc=UY4W2K3M3T
 * https//appsmeetzcom/redeemvouchersvcUY4W2K3M3T
 * https://app.smeetz.com/redeem-vouchers?vc=572ZS64YLH_old_236503
 *
 */
export function extractCodeFromUrl(value: string): string {
  const promoCodeMatch = value.match(/vc=?([A-Za-z0-9_-]+)/);
  if (promoCodeMatch) {
    return promoCodeMatch[1];
  }
  return "";
}

export function getPromocodeInputPlaceholder(type: PaymentMethods): string {
  if (type === PaymentMethods.POS_GIFT_CARD) {
    return t("promocode.enter-gift-card");
  }
  if (type === PaymentMethods.POS_MEMBERSHIP) {
    return t("promocode.enter-membership");
  }
  return t("promocode.enter");
}

/**
 * Pull and store promocodeCampaigns linked to the selected POS config
 */
export async function pullAndStorePromocodeCampaigns(signal?: AbortSignal) {
  const posConfigId = POSConfigModule.selectedConfig?.id;
  if (!posConfigId) {
    return;
  }

  LogService.info(
    `Pulling and storing promocode campaigns linked to the selected POS config: ${posConfigId}`
  );

  const allCampaigns: IPromocodeCampaign[] = [];
  const limit = 100;
  let skip = 0;
  // eslint-disable-next-line
	while (true) {
    if (signal?.aborted) break;
    const campaings = await getPromocodeCampaginsByConfigId(posConfigId, {
      skip,
      limit,
    });
    if (!campaings || !campaings.length) {
      break;
    }
    // pull the pictures of the campaigns
    const ids = campaings
      .filter((c) => c?.PromoCodeCampaignRead?.id)
      .map((c) => Number(c?.PromoCodeCampaignRead?.id));
    const pictures = await getSmeetzPictures(
      SmeetzPictureEntityTypes.PromoCode,
      ids
    );

    skip += campaings.length;

    const now = dayjs();
    for (const campaign of campaings) {
      const c = campaign?.PromoCodeCampaignRead;
      const endDateTime = c?.endDateTime;
      if (!c || c.cancelled === 1 || dayjs(endDateTime).isBefore(now)) continue;
      const campaignPicture = pictures.find((p) => p?.entityId === c.id);
      const picture = campaignPicture
        ? createCroppedImageproxyUrl({
            name: campaignPicture?.name,
            table: SmeetzPictureEntityTables.PromoCode,
            metaData: campaignPicture?.metaData,
          })
        : "";
      allCampaigns.push({
        id: Number(c?.id),
        posConfigId: Number(campaign?.posConfigId),
        name: String(c?.name),
        startDateTime: String(c?.startDateTime),
        endDateTime: String(c?.endDateTime),
        codes: c?.codes?.map((p) => String(p?.code || p?.codeUnique)) || [],
        isPinned: Boolean(campaign?.pinned),
        flatValue: Number(c?.flatValue),
        percentageValue: Number(c?.percentValue),
        picture: picture,
        position: campaign?.position ?? 25,
      });
    }
  }

  // Atomically replace the promocode campaigns table in Dexie
  // If this fails, the old data remains intact
  if (!signal?.aborted) {
    await atomicTableSwap(dexieDB.promocodeCampaigns as any, allCampaigns);
  }

  LogService.info(
    `Done pulling and storing promocode campaigns linked to the selected POS config: ${posConfigId}`
  );
}
